import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Building2, Calendar, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const GlobalSearch = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Focus input when opened
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    // Debounced search
    if (query.length < 2) {
      setResults(null);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
        setResults(response.data);
      } catch (error) {
        console.error('Search error:', error);
      }
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleBusinessClick = (business) => {
    // Deep-link by name + view so the directory's search filter surfaces it
    // (the directory has no ?business= handler / no detail route).
    const q = encodeURIComponent(business.business_name || '');
    navigate(`/directory?search=${q}${business.btc_map ? '&view=btcmap' : ''}`);
    setIsOpen(false);
    setQuery('');
    onClose?.();
  };

  const handleEventClick = (event) => {
    navigate('/events');
    setIsOpen(false);
    setQuery('');
    onClose?.();
  };

  // Keyboard shortcut to open search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground bg-muted rounded-lg hover:bg-muted/80 transition-colors"
        data-testid="search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline">Search</span>
        <kbd className="hidden md:inline-flex h-5 items-center gap-1 rounded border bg-background px-1.5 text-[10px] font-medium text-muted-foreground">
          ⌘K
        </kbd>
      </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50" 
        onClick={() => { setIsOpen(false); setQuery(''); }}
      />
      
      {/* Search Modal */}
      <div className="fixed inset-x-4 top-20 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-lg z-50">
        <div className="bg-background rounded-xl shadow-2xl border border-border overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b border-border">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search businesses, events..."
              className="flex-1 bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
              data-testid="search-input"
            />
            {loading && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
            <button onClick={() => { setIsOpen(false); setQuery(''); }}>
              <X className="w-5 h-5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>

          {/* Results */}
          {results && (
            <div className="max-h-80 overflow-y-auto">
              {results.total === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No results found for "{query}"
                </div>
              ) : (
                <>
                  {/* Businesses */}
                  {results.businesses.length > 0 && (
                    <div className="p-2">
                      <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                        Businesses
                      </div>
                      {results.businesses.map((business) => (
                        <button
                          key={business.id}
                          onClick={() => handleBusinessClick(business)}
                          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                        >
                          <Building2 className="w-4 h-4 text-orange-500" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{business.business_name || business.name}</div>
                            <div className="text-sm text-muted-foreground truncate">
                              {Array.isArray(business.category) ? business.category[0] : business.category} • {business.city || business.region}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Events */}
                  {results.events.length > 0 && (
                    <div className="p-2 border-t border-border">
                      <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                        Events
                      </div>
                      {results.events.map((event) => (
                        <button
                          key={event.id}
                          onClick={() => handleEventClick(event)}
                          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                        >
                          <Calendar className="w-4 h-4 text-purple-500" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{event.title}</div>
                            <div className="text-sm text-muted-foreground">
                              {event.event_date} • {event.city}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Empty state */}
          {!results && query.length < 2 && (
            <div className="p-4 text-center text-muted-foreground text-sm">
              Type at least 2 characters to search
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GlobalSearch;
